import {
	type OrgSlugParams,
	PortalConfigSchema,
	orgSlugParamsSchema,
} from "@/lib/portal-schemas";
import { Button, JsonEditor } from "@repo/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

const WEB_API_BASE =
	(import.meta.env.VITE_WEB_API_BASE as string) || "http://localhost:8080";
const ATTORNEY_API_BASE =
	(import.meta.env.VITE_ATTORNEY_API_URL as string) || "http://localhost:8081";
const USE_SAMPLE =
	((import.meta.env.VITE_USE_SAMPLE as string) || "true") === "true";

const SAMPLE_PORTAL = {
	title: "Editable Portal Draft",
	theme: { mode: "light", accent: "blue" },
	blocks: [
		{
			type: "hero",
			heading: "Welcome",
			subheading: "Edit this portal configuration",
		},
	],
};

export default function PortalEditRoute() {
	const params = useParams({ strict: false }) as unknown as OrgSlugParams;
	const parsed = orgSlugParamsSchema.safeParse(params);
	if (!parsed.success) {
		return (
			<main style={{ padding: 24 }}>
				<h1>Invalid parameters</h1>
				<p>Expected org/slug with alphanumeric and dashes.</p>
				<p>
					<Link to="/">Go home</Link>
				</p>
			</main>
		);
	}
	const { org, slug } = parsed.data;

	const { data, isLoading, error } = useQuery({
		queryKey: ["portal-config", org, slug],
		queryFn: async () => {
			if (USE_SAMPLE) return SAMPLE_PORTAL;
			const res = await fetch(
				`${WEB_API_BASE.replace(/\/$/, "")}/api/portal-config/${encodeURIComponent(org)}/${encodeURIComponent(slug)}`,
				{ headers: { Accept: "application/json" } },
			);
			if (!res.ok) throw new Error(`Failed to load portal: ${res.status}`);
			return (await res.json()) as Record<string, unknown>;
		},
	});

	const [json, setJson] = useState(() =>
		JSON.stringify(SAMPLE_PORTAL, null, 2),
	);

	useEffect(() => {
		if (data) setJson(JSON.stringify(data, null, 2));
	}, [data]);

	const saveMutation = useMutation({
		mutationFn: async (payload: unknown) => {
			const res = await fetch(
				`${ATTORNEY_API_BASE.replace(/\/$/, "")}/api/attorneys/portals/${encodeURIComponent(org)}/${encodeURIComponent(slug)}/drafts`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify(payload),
				},
			);
			if (!res.ok) throw new Error(`Save failed: ${res.status}`);
			return await res.json();
		},
	});

	const publishMutation = useMutation({
		mutationFn: async (payload: unknown) => {
			const res = await fetch(
				`${ATTORNEY_API_BASE.replace(/\/$/, "")}/api/attorneys/portals/${encodeURIComponent(org)}/${encodeURIComponent(slug)}/publish`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify(payload),
				},
			);
			if (!res.ok) throw new Error(`Publish failed: ${res.status}`);
			return await res.json();
		},
	});

	if (isLoading)
		return (
			<main style={{ padding: 24 }}>
				<p>Loading…</p>
			</main>
		);
	if (error)
		return (
			<main style={{ padding: 24 }}>
				<p>Error: {(error as Error).message}</p>
			</main>
		);

	const onSave = () => {
		try {
			const parsed = PortalConfigSchema.parse(JSON.parse(json));
			saveMutation.mutate(parsed);
		} catch (e) {
			alert(`Invalid JSON: ${(e as Error).message}`);
		}
	};

	const onPublish = () => {
		try {
			const parsed = PortalConfigSchema.parse(JSON.parse(json));
			// Typically publish would be a separate endpoint with versioning
			publishMutation.mutate(parsed);
		} catch (e) {
			alert(`Invalid JSON: ${(e as Error).message}`);
		}
	};

	return (
		<main style={{ padding: 24, display: "grid", gap: 16 }}>
			<header
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div>
					<h1>
						Edit Portal — {org}/{slug}
					</h1>
					<p>
						<Link to="/portals/$org/$slug/preview" params={{ org, slug }}>
							Preview
						</Link>
					</p>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					<Button onClick={onSave} disabled={saveMutation.isPending}>
						Save Draft
					</Button>
					<Button
						onClick={onPublish}
						disabled={publishMutation.isPending}
						variant="default"
					>
						Publish
					</Button>
				</div>
			</header>

			<section>
				<JsonEditor
					value={json}
					onChange={setJson}
					rows={18}
					helperText="Edit the portal JSON. Save as draft or publish when ready."
				/>
			</section>
		</main>
	);
}
